import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, QrCode, Users, LogOut, Menu, X, Key } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import ChangePasswordModal from '../components/ChangePasswordModal';

const StaffLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/staff/login');
  };

  const navItems = [
    { path: '/staff/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/staff/scanner', icon: QrCode, label: 'QR Scanner' },
    { path: '/staff/customers', icon: Users, label: 'Customers' },
  ];

  return (
    <div className="min-h-screen bg-[#FFFFF0] flex">
      {/* Mobile menu button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-lg bg-[#4A154B] text-white"
        data-testid="mobile-menu-toggle"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40 w-64 gradient-plum transform transition-transform duration-300
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full p-6">
          {/* Logo */}
          <div className="mb-10">
            <h1 className="font-serif text-2xl text-white">Salon O</h1>
            <p className="text-xs text-[#C9A689] tracking-widest uppercase">Staff Portal</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2">
            {navItems.map(({ path, icon: Icon, label }) => (
              <NavLink
                key={path}
                to={path}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
                data-testid={`staff-nav-${label.toLowerCase().replace(' ', '-')}`}
              >
                <Icon size={20} strokeWidth={1.5} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User info & Logout */}
          <div className="border-t border-white/20 pt-6">
            <div className="mb-4">
              <p className="text-white/60 text-sm">Logged in as</p>
              <p className="text-white font-medium">{user?.name || 'Staff'}</p>
              <p className="text-[#C9A689] text-sm capitalize">{user?.role}</p>
            </div>
            <button
              onClick={() => setShowPasswordModal(true)}
              className="sidebar-item w-full text-white/70 hover:text-white hover:bg-white/10"
              title="Change Password"
            >
              <Key size={20} strokeWidth={1.5} />
              <span>Change Password</span>
            </button>
            <button
              onClick={handleLogout}
              className="sidebar-item w-full text-red-300 hover:text-red-200 hover:bg-red-500/20"
              data-testid="staff-logout-btn"
            >
              <LogOut size={20} strokeWidth={1.5} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-0 min-h-screen">
        <div className="p-4 lg:p-8">
          <Outlet />
        </div>
      </main>

      <ChangePasswordModal isOpen={showPasswordModal} onClose={() => setShowPasswordModal(false)} />
    </div>
  );
};

export default StaffLayout;
